// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, type AlertProps } from '@imprivata-cloud/design-system';
import { isObject } from '@/utils/utils';

const TRANSLATION_ROOT = 'self-service.auth-method.otp';
const ALERT_AUTO_CLEAR_MS = 15000;

export const OTP_ENROLLMENT_ALERT_KEYS = {
  SENT: 'sent',
  WRONG: 'invalid-otp',
  INVALIDATED: 'max-attempts-exceeded',
} as const;

export type AlertKeys = (typeof OTP_ENROLLMENT_ALERT_KEYS)[keyof typeof OTP_ENROLLMENT_ALERT_KEYS];

type AlertConfig = { status: AlertProps['status'], titleKey: string };

export const OTP_ENROLLMENT_ALERT_CONFIG = {
  [OTP_ENROLLMENT_ALERT_KEYS.SENT]: { status: 'info', titleKey: `${TRANSLATION_ROOT}.send-banner` },
  [OTP_ENROLLMENT_ALERT_KEYS.WRONG]: { status: 'error', titleKey: `${TRANSLATION_ROOT}.wrong-banner` },
  [OTP_ENROLLMENT_ALERT_KEYS.INVALIDATED]: { status: 'error', titleKey: `${TRANSLATION_ROOT}.invalidated-banner` },
} satisfies Record<AlertKeys, AlertConfig>;

type OtpEnrollmentAlertsProps = {
  alertKey: AlertKeys | null;
  onClear: () => void;
  className?: string;
};

export const mapErrorCodeToAlert = (error: unknown): AlertKeys | null => {
  if (!isObject(error) || typeof error.code !== 'string') {
    return null;
  }
  
  const validCodes = Object.values(OTP_ENROLLMENT_ALERT_KEYS) as string[];
  return validCodes.includes(error.code) ? (error.code as AlertKeys) : null;
}

export const OtpEnrollmentAlerts = ({ alertKey, onClear, className }: OtpEnrollmentAlertsProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (alertKey === null) {
      return undefined;
    }

    const timeoutId = window.setTimeout(onClear, ALERT_AUTO_CLEAR_MS);

    return () => window.clearTimeout(timeoutId);
  }, [alertKey, onClear]);

  if (alertKey === null) {
    return null;
  }

  const alertConfig = OTP_ENROLLMENT_ALERT_CONFIG[alertKey];

  return (
    <div className={className}>
      <Alert status={alertConfig.status} title={t(alertConfig.titleKey)} onClose={onClear} />
    </div>
  );
};
